import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import InitialDataStep from "./components/InitialDataStep";
import ResultsStep from "./components/ResultsStep";
import ProgressIndicator from "./components/ProgressIndicator";
import { formatNumber } from "../../components/utils/format";
import { logEvent, TRACKING_EVENTS } from "../../lib/analytics";
import { useConfig } from "../../context/ConfigContext";

const SolarCalculator = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const {
    COMPANY_NAME,
    PLANTAS_SOLARES,
    ENERGIA_TOTAL,
    AHORRO_PROMEDIO,
    GARANTY_YEARS,
  } = config;

  const [currentStep, setCurrentStep] = useState(1);
  const [calculatorData, setCalculatorData] = useState({
    location: null,
    consumption: null,
    systemDesign: null,
    financing: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const steps = [
    {
      id: "initial-data",
      title: "Consumo",
      description: "Región y energía",
      longDescription:
        "Selecciona tu región e ingresa tu consumo energético",
    },
    {
      id: "results",
      title: "Resultados",
      description: "Tu propuesta",
      longDescription:
        "Revisa tu propuesta personalizada y solicita una consulta",
    },
  ];

  useEffect(() => {
    // Load progress from localStorage
    const savedProgress = localStorage.getItem("calculatorProgress");
    if (savedProgress && !isSubmitted) {
      const progress = JSON.parse(savedProgress);
      setCalculatorData(progress?.data);
      setCurrentStep(progress?.step);
    }

    logEvent("Calculadora Solar", "abrir_calculadora", "Página Calculadora Solar");
  }, []);

  useEffect(() => {
    // Save progress to localStorage
    if (!isSubmitted) {
      localStorage.setItem(
        "calculatorProgress",
        JSON.stringify({
          step: currentStep,
          data: calculatorData,
        })
      );
    }
  }, [currentStep, calculatorData, isSubmitted]);

  const handleInitialDataSubmit = (data) => {
    setCalculatorData(data);
    setTimeout(() => setCurrentStep(2), 500);
  };

  const handleRequestConsultation = async (consultationData) => {
    setIsLoading(true);

    logEvent("Calculadora Solar", "enviar_calculadora_completa", "Enviar Leads");

    try {
      const { contact, system, financing, location, consumption } = consultationData;

      // 1. Prepare HTML for the automated email
      const emailHtml = `
        <div style="font-family: sans-serif; max-width: 600px; color: #333; line-height: 1.6;">
          <h2 style="color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px;">🚀 Nueva Solicitud de Consulta - ${COMPANY_NAME}</h2>
          
          <div style="background: #f8fafc; padding: 20px; border-radius: 12px; margin-bottom: 20px; border: 1px solid #e2e8f0;">
            <h3 style="margin-top: 0; color: #1e293b; font-size: 18px;">👤 Datos del Cliente</h3>
            <p style="margin: 5px 0;"><strong>Nombre:</strong> ${contact.name}</p>
            <p style="margin: 5px 0;"><strong>Email:</strong> ${contact.email}</p>
            <p style="margin: 5px 0;"><strong>Teléfono:</strong> ${contact.phone}</p>
            <p style="margin: 5px 0;"><strong>Preferencia:</strong> ${contact.preferredContact}</p>
          </div>

          <div style="background: #f0fdf4; padding: 20px; border-radius: 12px; margin-bottom: 20px; border: 1px solid #dcfce7;">
            <h3 style="margin-top: 0; color: #166534; font-size: 18px;">⚡ Propuesta Técnica</h3>
            <p style="margin: 5px 0;"><strong>📍 Región:</strong> ${location.zoneName}</p>
            <p style="margin: 5px 0;"><strong>📊 Consumo:</strong> ${consumption.monthlyKwh} kWh/mes</p>
            <p style="margin: 5px 0;"><strong>☀️ Sistema:</strong> ${system.systemSize} kW (${system.panels} paneles)</p>
          </div>

          <div style="background: #eff6ff; padding: 20px; border-radius: 12px; margin-bottom: 20px; border: 1px solid #dbeafe;">
            <h3 style="margin-top: 0; color: #1e40af; font-size: 18px;">💰 Resumen Financiero</h3>
            <p style="margin: 5px 0;"><strong>Inversión Neta:</strong> ${financing.totalCost.toLocaleString('es-GT', { style: 'currency', currency: 'GTQ' })}</p>
            <p style="margin: 5px 0;"><strong>Ahorro Mensual:</strong> ${system.monthlySavings.toLocaleString('es-GT', { style: 'currency', currency: 'GTQ' })}</p>
            <p style="margin: 5px 0;"><strong>Ahorro 25 años:</strong> ${financing.totalSavings25Years.toLocaleString('es-GT', { style: 'currency', currency: 'GTQ' })}</p>
            <p style="margin: 5px 0;"><strong>Tiempo Recuperación:</strong> ${system.paybackYears} años</p>
          </div>

          <p style="font-size: 12px; color: #94a3b8; text-align: center; margin-top: 30px;">
            Este es un mensaje automático generado por la Calculadora Solar de ${COMPANY_NAME}.
          </p>
        </div>
      `;

      // 2. Load Firestore
      const { collection, addDoc, serverTimestamp } = await import("firebase/firestore");
      const { db } = await import("../../lib/firebase");

      if (!db) throw new Error("Database not initialized");

      // 3. Save to 'mail' collection (Trigger Email Extension)
      await addDoc(collection(db, "mail"), {
        to: config.EMAIL_ADDRESS,
        message: {
          subject: `☀️ Nuevo Lead Solar: ${contact.name}`,
          html: emailHtml,
        },
        metadata: {
          clientName: contact.name,
          clientPhone: contact.phone,
          type: "solar_calculator_lead"
        },
        createdAt: serverTimestamp(),
      });

      // 4. Also save to a general 'leads' collection for record keeping
      await addDoc(collection(db, "leads_calculadora"), {
        ...consultationData,
        createdAt: serverTimestamp(),
      });

      // Instead of alert, set success state
      setIsSubmitted(true);
      localStorage.removeItem("calculatorProgress");
    } catch (error) {
      console.error("Error requesting consultation:", error);
      alert("Hubo un error al enviar la solicitud. Por favor, intenta de nuevo o contáctanos directamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetCalculator = () => {
    setCalculatorData({
      location: null,
      consumption: null,
      systemDesign: null,
      financing: null,
    });
    setCurrentStep(1);
    setIsSubmitted(false);
    localStorage.removeItem("calculatorProgress");
  };

  const SuccessView = () => (
    <div className="text-center py-12 px-4 animate-in fade-in zoom-in duration-500">
      <div className="mb-6 relative inline-block">
        <div className="absolute inset-0 bg-green-100 rounded-full animate-ping opacity-25"></div>
        <div className="relative bg-green-50 rounded-full p-6 text-green-600">
          <Icon name="CheckCircle" size={64} strokeWidth={1.5} />
        </div>
      </div>

      <h2 className="text-3xl font-bold text-foreground mb-4">
        ¡Solicitud Recibida con Éxito!
      </h2>

      <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
        Gracias por confiar en <strong>{COMPANY_NAME}</strong>. Tu información técnica ha sido enviada a nuestro departamento de ingeniería.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 max-w-4xl mx-auto">
        <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 shadow-sm">
          <Icon name="Clock" className="text-primary mx-auto mb-3" size={24} />
          <h4 className="font-bold text-sm text-foreground uppercase tracking-wider mb-1">Tiempo de Respuesta</h4>
          <p className="text-muted-foreground">Lo antes posible</p>
        </div>
        <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 shadow-sm">
          <Icon name="PhoneCall" className="text-primary mx-auto mb-3" size={24} />
          <h4 className="font-bold text-sm text-foreground uppercase tracking-wider mb-1">Contacto Directo</h4>
          <p className="text-muted-foreground">Un asesor te contactará personalmente</p>
        </div>
        <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 shadow-sm">
          <Icon name="FileText" className="text-primary mx-auto mb-3" size={24} />
          <h4 className="font-bold text-sm text-foreground uppercase tracking-wider mb-1">Siguiente Paso</h4>
          <p className="text-muted-foreground">Agendaremos una visita técnica gratuita</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <Button
          variant="primary"
          size="lg"
          onClick={() => navigate("/")}
          iconName="Home"
        >
          Volver al Inicio
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={resetCalculator}
          iconName="RotateCcw"
        >
          Nueva Consulta
        </Button>
      </div>

      <p className="mt-12 text-sm text-muted-foreground flex items-center justify-center gap-2">
        <Icon name="ShieldCheck" size={16} className="text-green-600" />
        Tus datos están protegidos bajo nuestra política de privacidad
      </p>
    </div>
  );

  const renderCurrentStep = () => {
    if (isSubmitted) return <SuccessView />;

    switch (currentStep) {
      case 1:
        return (
          <InitialDataStep
            onDataSubmit={handleInitialDataSubmit}
            initialData={calculatorData}
          />
        );
      case 2:
        return (
          <ResultsStep
            locationData={calculatorData?.location}
            consumptionData={calculatorData?.consumption}
            systemDesign={calculatorData?.systemDesign}
            financingData={calculatorData?.financing}
            onRequestConsultation={handleRequestConsultation}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        {/* Hero Section */}
        {!isSubmitted && (
          <div className="bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                  Calculadora Solar Guatemala
                </h1>
                <p className="text-xl text-muted-foreground mb-6 max-w-3xl mx-auto">
                  Descubre cuánto puedes ahorrar con energía solar. Cálculo
                  personalizado para las condiciones climáticas y tarifas
                  eléctricas de Guatemala.
                </p>

                <div className="flex flex-wrap justify-center gap-6 text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon
                      name="CheckCircle"
                      size={16}
                      className="text-green-600"
                    />
                    <span>Cálculo gratuito y sin compromiso</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon
                      name="CheckCircle"
                      size={16}
                      className="text-green-600"
                    />
                    <span>Resultados en tiempo real</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon
                      name="CheckCircle"
                      size={16}
                      className="text-green-600"
                    />
                    <span>Consulta técnica gratuita</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calculator Content */}
        <div className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${isSubmitted ? 'py-20' : 'py-12'}`}>
          {/* Progress Indicator */}
          {!isSubmitted && (
            <>
              <ProgressIndicator
                currentStep={currentStep}
                totalSteps={steps?.length}
                steps={steps}
              />

              {/* Navigation Controls */}
              <div className="flex justify-between items-center mb-8">
                <div className="flex space-x-2">
                  {currentStep > 1 && (
                    <Button
                      variant="outline"
                      onClick={goToPreviousStep}
                      iconName="ArrowLeft"
                      iconPosition="left"
                    >
                      Anterior
                    </Button>
                  )}
                </div>

                <div className="flex space-x-2">
                  <Button
                    variant="ghost"
                    onClick={resetCalculator}
                    iconName="RotateCcw"
                    iconPosition="left"
                    size="sm"
                  >
                    Reiniciar
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Step Content */}
          <div className={`${!isSubmitted ? 'bg-white rounded-2xl solar-shadow-card p-8' : ''}`}>
            {isLoading ? (
              <div className="bg-white rounded-2xl solar-shadow-card p-12 text-center">
                <Icon
                  name="Loader2"
                  size={48}
                  className="text-primary mx-auto mb-4 animate-spin"
                />
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  Procesando tu Solicitud
                </h3>
                <p className="text-muted-foreground">
                  Estamos enviando tu información a nuestro equipo de
                  expertos...
                </p>
              </div>
            ) : (
              renderCurrentStep()
            )}
          </div>

          {/* Quick Stats */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-white rounded-xl border">
              <Icon
                name="SolarPanel"
                size={32}
                className="text-primary mx-auto mb-3"
              />
              <div className="text-2xl font-bold text-foreground mb-1">
                {PLANTAS_SOLARES}+
              </div>
              <div className="text-sm text-muted-foreground">
                Plantas instaladas
              </div>
            </div>

            <div className="text-center p-6 bg-white rounded-xl border">
              <Icon
                name="Zap"
                size={32}
                className="text-primary mx-auto mb-3"
              />
              <div className="text-2xl font-bold text-foreground mb-1">
                {formatNumber(ENERGIA_TOTAL)}W
              </div>
              <div className="text-sm text-muted-foreground">
                Capacidad instalada
              </div>
            </div>

            <div className="text-center p-6 bg-white rounded-xl border">
              <Icon
                name="DollarSign"
                size={32}
                className="text-primary mx-auto mb-3"
              />
              <div className="text-2xl font-bold text-foreground mb-1">
                {AHORRO_PROMEDIO}%
              </div>
              <div className="text-sm text-muted-foreground">
                Ahorro promedio
              </div>
            </div>

            <div className="text-center p-6 bg-white rounded-xl border">
              <Icon
                name="Award"
                size={32}
                className="text-primary mx-auto mb-3"
              />
              <div className="text-2xl font-bold text-foreground mb-1">
                {GARANTY_YEARS} años
              </div>
              <div className="text-sm text-muted-foreground">Garantía</div>
            </div>
          </div>

          {/* Trust Indicators  - Respaldados */}
          <div className="mt-12 bg-muted/30 rounded-xl p-8">
            <h3 className="text-xl font-semibold text-foreground text-center mb-6">
              Respaldados por las Mejores Marcas
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-60">
              <div className="text-center">
                <div className="bg-white rounded-lg p-4 mb-2">
                  <Icon
                    name="Sun"
                    size={32}
                    className="text-gray-600 mx-auto"
                  />
                </div>
                <p className="text-sm font-medium">Risen Energy</p>
                <p className="text-sm font-medium">JA Solar</p>
              </div>

              <div className="text-center">
                <div className="bg-white rounded-lg p-4 mb-2">
                  <Icon
                    name="Zap"
                    size={32}
                    className="text-gray-600 mx-auto"
                  />
                </div>
                <p className="text-sm font-medium">Huawei</p>
              </div>

              <div className="text-center">
                <div className="bg-white rounded-lg p-4 mb-2">
                  <Icon
                    name="Battery"
                    size={32}
                    className="text-gray-600 mx-auto"
                  />
                </div>
                <p className="text-sm font-medium">Chiko Solar</p>
              </div>

              <div className="text-center">
                <div className="bg-white rounded-lg p-4 mb-2">
                  <Icon
                    name="Shield"
                    size={32}
                    className="text-gray-600 mx-auto"
                  />
                </div>
                <p className="text-sm font-medium">Garantía Total</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="bg-secondary text-secondary-foreground py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm opacity-80">
              © {new Date()?.getFullYear()} {COMPANY_NAME} Guatemala. Todos los
              derechos reservados.
            </p>
            <p className="text-xs opacity-60 mt-2">
              Calculadora solar calibrada para las condiciones climáticas y
              regulaciones de Guatemala
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SolarCalculator;
